/*
 * 1-Minute Maths
 * Copyright © 2021 - 2022 White Rose Maths
 * categories.ts - question categories
*/

import * as colours from './colours';
import { ImageRequire } from './types';

export enum TopicCategoryName {
  ADDITION = 'Addition',
  MULTIPLICATION = 'Multiplication',
  SUBTRACTION = 'Subtraction',
  DIVISION = 'Division',
  SUBITISING = 'Subitising',
}

export enum SubitisingCategoryName {
  DICE_FORMATION_1 = '1 to 5 (Dice)',
  PERCEPTUAL_FORMATION_1 = '1 to 5 (Common)',
  RANDOM_FORMATION_1 = '1 to 5 (Random)',
  TWO_COLOURS_1 = '1 to 5 (Two colours)',
  OBJECT_FORMATION_1 = '1 to 5 (Objects)',
  FIVE_FRAME_1 = 'Five frame',
  TEN_FRAME_1 = 'Ten frame',
  FIVE_TO_NINE_1 = '5 to 9 (Common)',
  ONE_TO_NINE_1 = '1 to 9 (Common)',
  MAGIC_MIX_SUBITISING_1 = 'Magic mix to 5',
}
export function isSubitisingCategoryName(name: QuestionCategoryName): name is SubitisingCategoryName {
  return Object.values(SubitisingCategoryName).some(it => it === name);
}

export enum AdditionCategoryName {
  ADDING_0_1_2 = 'Add 0, 1 or 2',
  BONDS_TO_10 = 'Bonds to 10',
  BONDS_TO_20 = 'Bonds to 20',
  BONDS_TO_100 = 'Bonds to 100 (tens)',
  BONDS_TO_100_ONES = 'Bonds to 100 (tens and ones)',
  ADDING_10 = 'Add 10',
  DOUBLES = 'Doubles',
  NEAR_DOUBLES = 'Near doubles',
  MAGIC_MIX_10_ADDITION = 'Magic mix to 10',
  MAGIC_MIX_20_ADDITION = 'Magic mix to 20',
  MAGIC_MIX_100_ADDITION = 'Magic mix to 100',
  ADD_ACROSS_10 = 'Add across 10',
  ADD_ONES_WITHIN_20 = 'Add ones within 20',
  ADD_ONES_WITHIN_100 = 'Add ones within 100',
  ADD_WITHIN_10 = 'Add within 10',
  ADD_TWO_TENS_WITHIN_100 = 'Add two tens within 100',
  ADD_TENS_WITHIN_100 = 'Add tens within 100',
}
export function isAdditionCategoryName(name: QuestionCategoryName): name is AdditionCategoryName {
  return Object.values(AdditionCategoryName).some(it => it === name);
}

export enum MultiplicationCategoryName {
  SEQUENCE_2 = 'Count in 2s',
  SEQUENCE_5 = 'Count in 5s',
  SEQUENCE_10 = 'Count in 10s',
  SEQUENCE_3 = 'Count in 3s',
  SEQUENCE_100 = 'Count in 100s',
  MULTIPLY_TWO = 'Multiply by 2',
  MULTIPLY_THREE = 'Multiply by 3',
  MULTIPLY_FOUR = 'Multiply by 4',
  MULTIPLY_FIVE = 'Multiply by 5',
  MULTIPLY_SIX = 'Multiply by 6',
  MULTIPLY_SEVEN = 'Multiply by 7',
  MULTIPLY_EIGHT = 'Multiply by 8',
  MULTIPLY_NINE = 'Multiply by 9',
  MULTIPLY_TEN = 'Multiply by 10',
  MULTIPLY_ELEVEN = 'Multiply by 11',
  MULTIPLY_TWELVE = 'Multiply by 12',
  TIMES_TABLE_FACTS_TWO = '2 times-table facts',
  TIMES_TABLE_FACTS_THREE = '3 times-table facts',
  TIMES_TABLE_FACTS_FOUR = '4 times-table facts',
  TIMES_TABLE_FACTS_FIVE = '5 times-table facts',
  TIMES_TABLE_FACTS_SIX = '6 times-table facts',
  TIMES_TABLE_FACTS_SEVEN = '7 times-table facts',
  TIMES_TABLE_FACTS_EIGHT = '8 times-table facts',
  TIMES_TABLE_FACTS_NINE = '9 times-table facts',
  TIMES_TABLE_FACTS_TEN = '10 times-table facts',
  TIMES_TABLE_FACTS_ELEVEN = '11 times-table facts',
  TIMES_TABLE_FACTS_TWELVE = '12 times-table facts',
  EQUAL_GROUPS_OBJECTS = 'Equal groups (Objects)',
  EQUAL_GROUPS_COUNTERS = 'Equal groups (Counters)',
  MAGIC_MIX_MULTIPLICATION = 'Multiplication magic mix',
}
export function isMultiplicationCategoryName(name: QuestionCategoryName): name is MultiplicationCategoryName {
  return Object.values(MultiplicationCategoryName).some(it => it === name);
}

export enum SubtractionCategoryName {
  SUBTRACT_1 = 'Subtract 1',
  SUBTRACT_2 = 'Subtract 2',
  DIFFERENCE = 'Near subtraction',
  MAGIC_MIX_10_SUBTRACTION = 'Magic mix in 10',
  MAGIC_MIX_20_SUBTRACTION = 'Magic mix in 20',
  MAGIC_MIX_100_SUBTRACTION = 'Magic mix in 100',
  SUBTRACT_FROM_10 = 'Subtract from 10',
  SUBTRACT_WITHIN_10 = 'Subtract within 10',
  SUBTRACT_ACROSS_10 = 'Subtract across 10 within 20',
  SUBTRACT_ACROSS_TENS = 'Subtract across tens within 100',
  SUBTRACT_ONES = 'Subtract ones within 100',
  SUBTRACT_10 = 'Subtract 10',
  SUBTRACT_TENS = 'Subtract tens',
}
export function isSubtractionCategoryName(name: QuestionCategoryName): name is SubtractionCategoryName {
  return Object.values(SubtractionCategoryName).some(it => it === name);
}

export enum DivisionCategoryName {
  HALVES = 'Halves',
  DIVIDE_2 = 'Divide by 2',
  DIVIDE_10 = 'Divide by 10',
  DIVIDE_5 = 'Divide by 5',
  TWO_TIMES_TABLE = '2 times-table',
  THREE_TIMES_TABLE = '3 times-table',
  FOUR_TIMES_TABLE = '4 times-table',
  FIVE_TIMES_TABLE = '5 times-table',
  SIX_TIMES_TABLE = '6 times-table',
  SEVEN_TIMES_TABLE = '7 times-table',
  EIGHT_TIMES_TABLE = '8 times-table',
  NINE_TIMES_TABLE = '9 times-table',
  TEN_TIMES_TABLE = '10 times-table',
  ELEVEN_TIMES_TABLE = '11 times-table',
  TWELVE_TIMES_TABLE = '12 times-table',
  ODD_EVEN = 'Odd & even numbers',
  MAGIC_MIX_DIVISION = 'Division Magic Mix'
}
export function isDivisionCategoryName(name: QuestionCategoryName): name is DivisionCategoryName {
  return Object.values(DivisionCategoryName).some(it => it === name);
}

export type QuestionCategoryName = 
  | SubitisingCategoryName
  | AdditionCategoryName
  | MultiplicationCategoryName
  | SubtractionCategoryName
  | DivisionCategoryName;

const AggregateCategoryNames = [
  SubitisingCategoryName.MAGIC_MIX_SUBITISING_1,
  AdditionCategoryName.MAGIC_MIX_10_ADDITION,
  AdditionCategoryName.MAGIC_MIX_20_ADDITION,
  AdditionCategoryName.MAGIC_MIX_100_ADDITION,
  MultiplicationCategoryName.MAGIC_MIX_MULTIPLICATION,
  SubtractionCategoryName.MAGIC_MIX_10_SUBTRACTION,
  SubtractionCategoryName.MAGIC_MIX_20_SUBTRACTION,
  SubtractionCategoryName.MAGIC_MIX_100_SUBTRACTION,
  DivisionCategoryName.MAGIC_MIX_DIVISION,
] as const;
export type AggregateCategoryName = typeof AggregateCategoryNames[number];

export function isAggregateCategoryName(name: QuestionCategoryName): name is AggregateCategoryName {
  return AggregateCategoryNames.some(it => it === name);
}

export interface TopicCategory {
  name: TopicCategoryName;
  color: string;
  buttonColor: string;
  categories: QuestionCategory[];
  icon: ImageRequire; 
  disabled: boolean;
}

export interface QuestionCategory {
  name: QuestionCategoryName,
  color: string
}

export const subitising_categories: QuestionCategory[] = [
  { name: SubitisingCategoryName.DICE_FORMATION_1, color: colours.topic1 },
  { name: SubitisingCategoryName.PERCEPTUAL_FORMATION_1, color: colours.topic2 },
  { name: SubitisingCategoryName.RANDOM_FORMATION_1, color: colours.topic3 },
  { name: SubitisingCategoryName.TWO_COLOURS_1, color: colours.topic4 },
  { name: SubitisingCategoryName.FIVE_FRAME_1, color: colours.topic5 },
  { name: SubitisingCategoryName.OBJECT_FORMATION_1, color: colours.topic6 },
  { name: SubitisingCategoryName.TEN_FRAME_1, color: colours.topic7 },
  { name: SubitisingCategoryName.FIVE_TO_NINE_1, color: colours.topic8 },
  { name: SubitisingCategoryName.ONE_TO_NINE_1, color: colours.topic9 },
  { name: SubitisingCategoryName.MAGIC_MIX_SUBITISING_1, color: colours.topic10 },
];
export const addition_categories: QuestionCategory[] = [
  { name: AdditionCategoryName.ADDING_0_1_2, color: colours.topic1 },
  { name: AdditionCategoryName.ADD_WITHIN_10, color: colours.topic2 },
  { name: AdditionCategoryName.BONDS_TO_10, color: colours.topic3 },
  { name: AdditionCategoryName.BONDS_TO_20, color: colours.topic4 },
  { name: AdditionCategoryName.ADDING_10, color: colours.topic5 },
  { name: AdditionCategoryName.ADD_ACROSS_10, color: colours.topic6 },
  { name: AdditionCategoryName.ADD_TENS_WITHIN_100, color: colours.topic7 },
  { name: AdditionCategoryName.ADD_TWO_TENS_WITHIN_100, color: colours.topic8 },
  { name: AdditionCategoryName.BONDS_TO_100, color: colours.topic9 },
  { name: AdditionCategoryName.BONDS_TO_100_ONES, color: colours.topic10 },
  { name: AdditionCategoryName.DOUBLES, color: colours.topic11 },
  { name: AdditionCategoryName.NEAR_DOUBLES, color: colours.topic12 },
  { name: AdditionCategoryName.ADD_ONES_WITHIN_20, color: colours.topic13 },
  { name: AdditionCategoryName.ADD_ONES_WITHIN_100, color: colours.topic14 },
  { name: AdditionCategoryName.MAGIC_MIX_10_ADDITION, color: colours.topic15 },
  { name: AdditionCategoryName.MAGIC_MIX_20_ADDITION, color: colours.topic16 },
  { name: AdditionCategoryName.MAGIC_MIX_100_ADDITION, color: colours.topic17 },
];

export const multiplication_categories: QuestionCategory[] = [
  { name: MultiplicationCategoryName.SEQUENCE_2, color: colours.topic1 },
  { name: MultiplicationCategoryName.SEQUENCE_5, color: colours.topic2 },
  { name: MultiplicationCategoryName.SEQUENCE_10, color: colours.topic3 },
  { name: MultiplicationCategoryName.SEQUENCE_3, color: colours.topic4 },
  { name: MultiplicationCategoryName.SEQUENCE_100, color: colours.topic5 },
  { name: MultiplicationCategoryName.EQUAL_GROUPS_OBJECTS, color: colours.topic6 },
  { name: MultiplicationCategoryName.EQUAL_GROUPS_COUNTERS, color: colours.topic7 },
  { name: MultiplicationCategoryName.MULTIPLY_TWO, color: colours.topic8 },
  { name: MultiplicationCategoryName.MULTIPLY_THREE, color: colours.topic9 },
  { name: MultiplicationCategoryName.MULTIPLY_FOUR, color: colours.topic10 },
  { name: MultiplicationCategoryName.MULTIPLY_FIVE, color: colours.topic11 },
  { name: MultiplicationCategoryName.MULTIPLY_SIX, color: colours.topic12 },
  { name: MultiplicationCategoryName.MULTIPLY_SEVEN, color: colours.topic13 },
  { name: MultiplicationCategoryName.MULTIPLY_EIGHT, color: colours.topic14 },
  { name: MultiplicationCategoryName.MULTIPLY_NINE, color: colours.topic15 },
  { name: MultiplicationCategoryName.MULTIPLY_TEN, color: colours.topic16 },
  { name: MultiplicationCategoryName.MULTIPLY_ELEVEN, color: colours.topic17 },
  { name: MultiplicationCategoryName.MULTIPLY_TWELVE, color: colours.topic18 },
  { name: MultiplicationCategoryName.TIMES_TABLE_FACTS_TWO, color: colours.topic19 },
  { name: MultiplicationCategoryName.TIMES_TABLE_FACTS_THREE, color: colours.topic1 },
  { name: MultiplicationCategoryName.TIMES_TABLE_FACTS_FOUR, color: colours.topic2 },
  { name: MultiplicationCategoryName.TIMES_TABLE_FACTS_FIVE, color: colours.topic3 },
  { name: MultiplicationCategoryName.TIMES_TABLE_FACTS_SIX, color: colours.topic4 },
  { name: MultiplicationCategoryName.TIMES_TABLE_FACTS_SEVEN, color: colours.topic5 },
  { name: MultiplicationCategoryName.TIMES_TABLE_FACTS_EIGHT, color: colours.topic6 },
  { name: MultiplicationCategoryName.TIMES_TABLE_FACTS_NINE, color: colours.topic7 },
  { name: MultiplicationCategoryName.TIMES_TABLE_FACTS_TEN, color: colours.topic8 },
  { name: MultiplicationCategoryName.TIMES_TABLE_FACTS_ELEVEN, color: colours.topic9 },
  { name: MultiplicationCategoryName.TIMES_TABLE_FACTS_TWELVE, color: colours.topic10 },
  { name: MultiplicationCategoryName.MAGIC_MIX_MULTIPLICATION, color: colours.topic11 }
];

export const subtraction_categories: QuestionCategory[] = [
  { name: SubtractionCategoryName.SUBTRACT_1, color: colours.topic1 },
  { name: SubtractionCategoryName.SUBTRACT_2, color: colours.topic2 },
  { name: SubtractionCategoryName.DIFFERENCE, color: colours.topic3 },
  { name: SubtractionCategoryName.SUBTRACT_FROM_10, color: colours.topic4 },
  { name: SubtractionCategoryName.SUBTRACT_WITHIN_10, color: colours.topic5 },
  { name: SubtractionCategoryName.SUBTRACT_10, color: colours.topic6 },
  { name: SubtractionCategoryName.SUBTRACT_ONES, color: colours.topic7 },
  { name: SubtractionCategoryName.SUBTRACT_TENS, color: colours.topic8 },
  { name: SubtractionCategoryName.SUBTRACT_ACROSS_10, color: colours.topic9 },
  { name: SubtractionCategoryName.SUBTRACT_ACROSS_TENS, color: colours.topic10 },
  { name: SubtractionCategoryName.MAGIC_MIX_10_SUBTRACTION, color: colours.topic11 },
  { name: SubtractionCategoryName.MAGIC_MIX_20_SUBTRACTION, color: colours.topic12 },
  { name: SubtractionCategoryName.MAGIC_MIX_100_SUBTRACTION, color: colours.topic13 },
];

export const division_categories: QuestionCategory[] = [
  { name: DivisionCategoryName.HALVES, color: colours.topic1},
  { name: DivisionCategoryName.DIVIDE_2, color: colours.topic2 },
  { name: DivisionCategoryName.DIVIDE_5, color: colours.topic3 },
  { name: DivisionCategoryName.DIVIDE_10, color: colours.topic4 },
  { name: DivisionCategoryName.TWO_TIMES_TABLE, color: colours.topic5 },
  { name: DivisionCategoryName.THREE_TIMES_TABLE, color: colours.topic6 },
  { name: DivisionCategoryName.FOUR_TIMES_TABLE, color: colours.topic7 },
  { name: DivisionCategoryName.FIVE_TIMES_TABLE, color: colours.topic8 },
  { name: DivisionCategoryName.SIX_TIMES_TABLE, color: colours.topic9 },
  { name: DivisionCategoryName.SEVEN_TIMES_TABLE, color: colours.topic10 },
  { name: DivisionCategoryName.EIGHT_TIMES_TABLE, color: colours.topic11 },
  { name: DivisionCategoryName.NINE_TIMES_TABLE, color: colours.topic12 },
  { name: DivisionCategoryName.TEN_TIMES_TABLE, color: colours.topic13 },
  { name: DivisionCategoryName.ELEVEN_TIMES_TABLE, color: colours.topic14 },
  { name: DivisionCategoryName.TWELVE_TIMES_TABLE, color: colours.topic15 },
  { name: DivisionCategoryName.ODD_EVEN, color: colours.topic16 },
  { name: DivisionCategoryName.MAGIC_MIX_DIVISION, color: colours.topic17 },
];

export const topic_categories: TopicCategory[] = [
  {
    name: TopicCategoryName.SUBITISING, color: colours.subitising, buttonColor: colours.subitisingButton, categories: subitising_categories, icon: require('./assets/images/HomePageSubitise_72dpi.png'), disabled: false
  },
  {
    name: TopicCategoryName.ADDITION, color: colours.addition, buttonColor: colours.additionButton, categories: addition_categories, icon: require('./assets/images/HomePagePlus_72dpi.png'), disabled: false
  },
  {
    name: TopicCategoryName.SUBTRACTION, color: colours.subtraction, buttonColor: colours.subtractionButton, categories: subtraction_categories, icon: require('./assets/images/HomePageMinus_72dpi.png'), disabled: false
  },
  {
    name: TopicCategoryName.MULTIPLICATION, color: colours.multiplication, buttonColor: colours.multiplicationButton, categories: multiplication_categories, icon: require('./assets/images/HomePageMultiply_72dpi.png'), disabled: false
  },
  {
    name: TopicCategoryName.DIVISION, color: colours.division, buttonColor: colours.divisionButton, categories: division_categories, icon: require('./assets/images/HomePageDivide_72dpi.png'), disabled: false
  },
];
